import { designAreaProblems, newDesignLayerId, normalizeDesignAnswer, resolveDesignAreaConfig, type DesignAreaAnswer, type DesignAreaConfig } from './design-area.js';

/** A shop-owned reusable composition, with physical source dimensions. */
export interface DesignerTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  sizeMm: { w: number; h: number };
  answer: DesignAreaAnswer;
}
export function parseDesignerTemplate(raw: unknown): DesignerTemplate {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Expected a reusable template');
  const t = raw as DesignerTemplate;
  if (Object.keys(t).some(k => !['id','name','description','category','sizeMm','answer'].includes(k)) || !/^custom:[a-zA-Z0-9_-]{1,60}$/.test(t.id) || typeof t.name !== 'string' || !t.name.trim() || t.name.length > 80 || (t.description !== undefined && (typeof t.description !== 'string' || t.description.length > 240)) || (t.category !== undefined && (typeof t.category !== 'string' || t.category.length > 40))) throw new Error('Invalid template identity or description');
  if (!t.sizeMm || Object.keys(t.sizeMm).some(k => !['w','h'].includes(k)) || ![t.sizeMm.w,t.sizeMm.h].every(n => typeof n === 'number' && Number.isFinite(n) && n >= 1 && n <= 2000)) throw new Error('Invalid template dimensions');
  const answer = normalizeDesignAnswer(t.answer);
  if (!answer || !answer.layers.length || answer.layers.length > 60 || answer.layers.length !== t.answer.layers.length) throw new Error('Template needs 1–60 valid layers');
  const clean = { v: 1 as const, layers: answer.layers, ...(answer.background?{background:answer.background}:{}) };
  if (designAreaProblems({ sizeMm: t.sizeMm, maxLayers:60, image:{maxCount:60}, text:{maxChars:2000,maxLines:100,minSizeMm:.5,maxSizeMm:2000} }, clean).length) throw new Error('Template contains an invalid layer');
  return { ...structuredClone(t), name: t.name.trim(), answer: clean };
}
export function fitDesignerTemplate(config: DesignAreaConfig, raw: unknown): DesignAreaAnswer {
  const t = parseDesignerTemplate(raw), cfg = resolveDesignAreaConfig(config);
  const margin = cfg.safeMarginMm;
  const k = Math.min((cfg.sizeMm.w - 2 * margin) / t.sizeMm.w, (cfg.sizeMm.h - 2 * margin) / t.sizeMm.h);
  if (k <= 0) throw new Error('The template does not fit this print zone');
  const groups = new Map<string, string>();
  const answer = structuredClone(t.answer);
  answer.layers.forEach(layer => {
    layer.id = newDesignLayerId();
    if (layer.groupId) { if (!groups.has(layer.groupId)) groups.set(layer.groupId, newDesignLayerId()); layer.groupId = groups.get(layer.groupId); }
    layer.box = { ...layer.box, x: cfg.sizeMm.w / 2 + (layer.box.x - t.sizeMm.w / 2) * k, y: cfg.sizeMm.h / 2 + (layer.box.y - t.sizeMm.h / 2) * k, w: layer.box.w * k, h: layer.box.h * k };
    if (layer.kind === 'text') { layer.sizeMm *= k; if (layer.textWidthMm) layer.textWidthMm *= k; }
    if (layer.kind === 'shape' && layer.stroke) layer.stroke.widthMm *= k;
    if (layer.pattern) {layer.pattern.gapXmm*=k;layer.pattern.gapYmm*=k;}
  });
  if (designAreaProblems({ ...config, sticker: undefined }, answer).length) throw new Error('This template is not compatible with the product rules');
  return answer;
}
/** Same configuration operation for Studio and MCP; releases freeze this library. */
export function configureDesignerTemplate(config: DesignAreaConfig, input: { template: unknown } | { removeId: string }): DesignAreaConfig {
  let templates = [...(config.templates ?? [])];
  if ('template' in input) {
    const t = parseDesignerTemplate(input.template);
    fitDesignerTemplate(config, t);
    templates = templates.filter(old => old.id !== t.id); templates.push(t);
    if (templates.length > 24) throw new Error('A product may offer up to 24 custom templates');
  } else {
    if (!/^custom:[a-zA-Z0-9_-]{1,60}$/.test(input.removeId)) throw new Error('Invalid template ID');
    templates = templates.filter(t => t.id !== input.removeId);
  }
  return { ...config, templates };
}
